/*
	ai_purchase.ts
  Copyright (c) 2013-present Human Cube Inc.
*/

import bfH from '../bfcore/bf_helper';
import { getNewZoneXY } from '../bfcore/bf_hitmap';
import { Game, PlayerPurchases } from '../bfcore/gameTypes';
import { WorkingData } from '../bfworking/workingTypes';


export function doPurchaseAndPlacement (working: WorkingData, game: Game): PlayerPurchases {
  // CAREFUL NOT TO MODIFY VALUES IN game, AS IT WILL CORRUPT IT (It might be a reference, not a copy).

  // Generates some default purchases and placements for a player.
  // Returns an array of purchases and placements.
  //     [[force type index, zone id to place at, x to place at, y to place at],
  //      [force type index, zone id to place at, x to place at, y to place at],
  //      [force type index, zone id to place at, x to place at, y to place at],
  //       ...
  //     ],

  const playerIndex = working.localPlayerIndex;
  const { foe, foeDistanceAir, foeDistanceLand } = working.ai.data!;

  const purchase: PlayerPurchases = [];
  const capitalZoneID: number = game.capital[playerIndex];
  if( capitalZoneID <= 0 )
    return purchase;

  let factoryTypeIndex = bfH.getForceTypeIndexByName(game, 'Factory');
  if (factoryTypeIndex >= 0 && !(game.canBuy[playerIndex] & (1 << factoryTypeIndex)))
      factoryTypeIndex = -1;

  let troopTypeIndex = bfH.getForceTypeIndexByName(game, 'Troop');
  if (troopTypeIndex >= 0 && !(game.canBuy[playerIndex] & (1 << troopTypeIndex)))
      troopTypeIndex = -1;

  let tankTypeIndex = bfH.getForceTypeIndexByName(game, 'Tank');
  if (tankTypeIndex >= 0 && !(game.canBuy[playerIndex] & (1 << tankTypeIndex)))
      tankTypeIndex = -1;

  let fighterTypeIndex = bfH.getForceTypeIndexByName(game, 'Fighter');
  if (fighterTypeIndex >= 0 && !(game.canBuy[playerIndex] & (1 << fighterTypeIndex)))
      fighterTypeIndex = -1;

  let transportTypeIndex = bfH.getForceTypeIndexByName(game, 'Transport');
  if (transportTypeIndex >= 0 && !(game.canBuy[playerIndex] & (1 << transportTypeIndex)))
      transportTypeIndex = -1;

  let cash = working.cash;
  let lastCash;

  if( factoryTypeIndex >= 0 ) {
    if( !bfH.zoneHasPlayerForce(game, capitalZoneID, playerIndex, factoryTypeIndex) ) {
      // No factory on capital, then try to build a factory before buying any other forces.
      if( game.cash[playerIndex] >= game.forceCost[factoryTypeIndex] ) {
        const pos = getNewZoneXY(game, capitalZoneID);
        purchase.push([factoryTypeIndex, capitalZoneID, pos.x, pos.y]);
      }
      return purchase;
    }
  }

  if (foeDistanceLand[capitalZoneID] <= 1 || foeDistanceAir[capitalZoneID] <= 1) {
    // There are enemy forces on or near the capital.
    // Only build at capital and buy only defensive forces!

    // Defense emergency! Buy only troops!
    do {
      lastCash = cash;
      if (troopTypeIndex >= 0 && cash >= game.forceCost[troopTypeIndex]) {
        const pos = getNewZoneXY(game, capitalZoneID);
        purchase.push([troopTypeIndex, capitalZoneID, pos.x, pos.y]);
        cash -= game.forceCost[troopTypeIndex];
      }
    } while (cash !== lastCash);

    // If unable to buy troops, then buy only tanks if able!
    do {
      lastCash = cash;
      if (tankTypeIndex >= 0 && cash >= game.forceCost[tankTypeIndex]) {
        const pos = getNewZoneXY(game, capitalZoneID);
        purchase.push([tankTypeIndex, capitalZoneID, pos.x, pos.y]);
        cash -= game.forceCost[tankTypeIndex];
      }
    } while (cash !== lastCash);

    return purchase;
  }

  const productionLocations: {zoneID: number, production: number, threats: number}[] = [];
  let mainlandAllFriendly = false;

  for(let zoneID = 1; zoneID <= game.zones; zoneID++) {
    const production = working.production[zoneID];
    let threats = foe[zoneID];
    for (let i = 0; i < game.bordersLand[zoneID].length; i++) {
      threats += foe[game.bordersLand[zoneID][i]];  // Include immediate bordering threats.
    }
    if(production > 0) {
      if(foeDistanceLand[zoneID] < 100) {
        mainlandAllFriendly = false;  // Still some foes on land connected to a production.
      }
      productionLocations.push({zoneID, production, threats });
    }
  }

  productionLocations.sort((a, b) => {  // Sort descending of threat value.
    if (a.threats > b.threats) {
      return -1;
    }
    if (a.threats < b.threats) {
      return 1;
    }
    return 0;
  });

  do {
    lastCash = cash;
    for(let location = 0; location < productionLocations.length; location++) {
      if(productionLocations[location].production > 0) {
        const zoneID = productionLocations[location].zoneID;

        if (productionLocations[location].threats > 0) {
          // This production location is currently threatened, so produce defensive forces only.

          if (mainlandAllFriendly) {
            if (transportTypeIndex >= 0 && cash >= game.forceCost[transportTypeIndex]) {
              if (game.bordersWater[zoneID].length > 0) {
                const waterBorderIndex = Math.floor(Math.random() * game.bordersWater[zoneID].length);
                const waterZoneID = game.bordersWater[zoneID][waterBorderIndex];
                if (foe[waterZoneID] === 0) {
                  const pos = getNewZoneXY(game, waterZoneID);
                  purchase.push([transportTypeIndex, waterZoneID, pos.x, pos.y]);
                  cash -= game.forceCost[transportTypeIndex];
                  productionLocations[location].production--;  // use up the production point.
                  continue;
                }
              }
            }
            if (fighterTypeIndex >= 0 && cash >= game.forceCost[fighterTypeIndex]) {
              const pos = getNewZoneXY(game, zoneID);
              purchase.push([fighterTypeIndex, zoneID, pos.x, pos.y]);
              cash -= game.forceCost[fighterTypeIndex];
              productionLocations[location].production--;  // use up the production point.
              continue;
            }
          }

          if (troopTypeIndex >= 0 && cash >= game.forceCost[troopTypeIndex]) {
            const pos = getNewZoneXY(game, zoneID);
            purchase.push([troopTypeIndex, zoneID, pos.x, pos.y]);
            cash -= game.forceCost[troopTypeIndex];
            productionLocations[location].production--;  // use up the production point.
            continue;
          }
          if( tankTypeIndex >= 0 && cash >= game.forceCost[tankTypeIndex] ) {
            const pos = getNewZoneXY(game, zoneID);
            purchase.push([tankTypeIndex, zoneID, pos.x, pos.y]);
            cash -= game.forceCost[tankTypeIndex];
            productionLocations[location].production--;  // use up the production point.
            continue;
          }
          continue;
        }

        // Purchase a mix of offensive/defensive based off of cash:

        if (mainlandAllFriendly) {
          if (transportTypeIndex >= 0 && cash >= game.forceCost[transportTypeIndex]) {
            if (game.bordersWater[zoneID].length > 0) {
              const waterBorderIndex = Math.floor(Math.random() * game.bordersWater[zoneID].length);
              const waterZoneID = game.bordersWater[zoneID][waterBorderIndex];
              if(foe[waterZoneID] === 0) {
                const pos = getNewZoneXY(game, waterZoneID);
                purchase.push([transportTypeIndex, waterZoneID, pos.x, pos.y]);
                cash -= game.forceCost[transportTypeIndex];
                productionLocations[location].production--;  // use up the production point.
                continue;
              }
            }
          }
          if (fighterTypeIndex >= 0 && cash >= game.forceCost[fighterTypeIndex]) {
            const pos = getNewZoneXY(game, zoneID);
            purchase.push([fighterTypeIndex, zoneID, pos.x, pos.y]);
            cash -= game.forceCost[fighterTypeIndex];
            productionLocations[location].production--;  // use up the production point.
            continue;
          }
        }

        if (productionLocations.length === 1 && bfH.isIsland(game, zoneID)) {
          // If only one production location and it is on an island, so try to buy a transport.
          // Buy a transport if possible and place in a random water zone neighbour.
          // TODO: have it select a water zone neighbour with friends or without foes.
          if (transportTypeIndex >= 0 && cash >= game.forceCost[transportTypeIndex]) {
            if (game.bordersWater[zoneID].length > 0) {
              const waterBorderIndex = Math.floor(Math.random() * game.bordersLand[zoneID].length);
              const waterZoneID = game.bordersWater[zoneID][waterBorderIndex];
              const pos = getNewZoneXY(game, waterZoneID);
              purchase.push([transportTypeIndex, waterZoneID, pos.x, pos.y]);
              cash -= game.forceCost[transportTypeIndex];
              productionLocations[location].production--;  // use up the production point.
              continue;
            }
          }
          // else fall through to normal purchases.
        }

        if (tankTypeIndex >= 0 && cash >= game.forceCost[tankTypeIndex]) {
          const pos = getNewZoneXY(game, zoneID);
          purchase.push([tankTypeIndex, zoneID, pos.x, pos.y]);
          cash -= game.forceCost[tankTypeIndex];
          productionLocations[location].production--;  // use up the production point.
          continue;
        }
        if (troopTypeIndex >= 0 && cash >= game.forceCost[troopTypeIndex]) {
          const pos = getNewZoneXY(game, zoneID);
          purchase.push([troopTypeIndex, zoneID, pos.x, pos.y]);
          cash -= game.forceCost[troopTypeIndex];
          productionLocations[location].production--;  // use up the production point.
          continue;
        }
      }
    }
  } while (cash !== lastCash);

  return purchase;
}
